import React, { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

/**
 * Consignment facts a page publishes for the `App_Messages` banner (ENVDB-7836).
 *
 * The banner renders in exactly one place — `ProtectedRoute`'s `withUserDetails` — which knows the
 * route but holds no consignment. Pages that do hold one (the wizard, summary, copy, device
 * movement) publish their selected forms and status here, and the single mount folds them into
 * its match criteria. Pages with nothing to publish do nothing, and the mount matches on route,
 * platform and dates alone.
 *
 * `route` stamps the publication with the route key it belongs to. The mount only uses a
 * publication whose route equals its own, so facts left behind by the previous page can never
 * leak onto the next one — the guard is structural, not a teardown that could be forgotten.
 */
export interface IAppMessagePublication {
    /** The route key these facts belong to, e.g. `/consignments/summary/:id`. */
    route: string;
    /** Selected form codes, dots stripped. */
    forms: string[];
    /** `ConsignmentStatus`; an unsaved consignment counts as DRAFT. */
    status: string;
}

export interface AppMessagesContextState {
    publication: IAppMessagePublication | null;
    publish: (publication: IAppMessagePublication | null) => void;
}

const defaultState: AppMessagesContextState = {
    publication: null,
    publish: () => {},
};

const AppMessagesContext = createContext<AppMessagesContextState>(defaultState);

export const AppMessagesProvider = ({ children }: { children: ReactNode }) => {
    const [publication, setPublication] = useState<IAppMessagePublication | null>(null);
    const publish = useCallback((next: IAppMessagePublication | null) => setPublication(next), []);
    const value = useMemo(() => ({ publication, publish }), [publication, publish]);

    return <AppMessagesContext.Provider value={value}>{children}</AppMessagesContext.Provider>;
};

/** The current publication, for the single mount. */
export const useAppMessagePublication = (): IAppMessagePublication | null =>
    useContext(AppMessagesContext).publication;

/**
 * Publish this page's consignment facts while it is mounted.
 *
 * Runs in a layout effect so the mount re-renders with the facts before the browser paints —
 * otherwise a `Forms`-filtered banner would appear one frame late. Clears on unmount so the next
 * page for the same route (summary of consignment B after consignment A) starts clean rather than
 * showing A's banner for a frame.
 */
export const usePublishAppMessageCriteria = (publication: IAppMessagePublication): void => {
    const { publish } = useContext(AppMessagesContext);
    const formsKey = publication.forms.join(',');

    useLayoutEffect(() => {
        publish(publication);
        return () => publish(null);
        // `publication` is represented by its primitive fields; formsKey stands in for the array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publish, publication.route, formsKey, publication.status]);
};
